import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Search, LogOut, Ruler, Instagram, Camera } from "lucide-react";
import { toast } from "sonner";
import { partnerApi, setPartnerToken, getPartnerToken, getPartnerAgency } from "../../lib/partnerApi";
import { Input } from "../../components/ui/input";

const NEW_WINDOW_HOURS = 72;

const isNew = (createdAt) => {
  if (!createdAt) return false;
  const t = new Date(createdAt).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t < NEW_WINDOW_HOURS * 3600 * 1000;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

export default function PartnerApplications() {
  const nav = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const agency = getPartnerAgency();

  useEffect(() => {
    if (!getPartnerToken()) {
      nav("/partner/login");
      return;
    }
    partnerApi
      .get("/partner/applications")
      .then((r) => setApps(Array.isArray(r.data) ? r.data : []))
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Failed to load applications");
      })
      .finally(() => setLoading(false));
  }, [nav]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return apps;
    return apps.filter((a) =>
      [a.full_name, a.reference, a.city, a.country, a.instagram]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle))
    );
  }, [apps, q]);

  const logout = () => {
    setPartnerToken(null);
    toast.success("Signed out");
    nav("/partner/login");
  };

  return (
    <div data-testid="partner-applications-page" className="min-h-screen bg-white text-[#0A0A0A]">
      {/* Top bar */}
      <div className="border-b border-black/10 sticky top-0 z-30 bg-white/90 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <Link to="/" className="font-serif italic text-2xl leading-none text-[#e83741]">
              ege
            </Link>
            <div className="hidden sm:block">
              <p className="label-overline text-zinc-500">Partner Portal</p>
              <p data-testid="partner-agency-name" className="text-sm font-medium">{agency || "Booking Agency"}</p>
            </div>
          </div>
          <button
            data-testid="partner-logout"
            onClick={logout}
            className="flex items-center gap-2 px-4 py-2 text-sm text-zinc-700 hover:bg-black/5"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-14">
        {/* Heading */}
        <div className="mb-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <p className="label-overline text-zinc-500">Scouting Applications</p>
            <h1 className="font-serif text-5xl mt-3">Incoming faces.</h1>
            <p className="text-zinc-600 mt-3 max-w-lg text-sm leading-relaxed">
              Every application submitted through egemodels.com. Contact details are hidden
              until the studio approves individual releases.
            </p>
          </div>
          <div className="relative w-full sm:w-80">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              data-testid="partner-search"
              placeholder="Search by name, city, reference…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"
            />
          </div>
        </div>

        {loading ? (
          <div className="py-24 flex items-center justify-center text-zinc-500">
            <Loader2 className="animate-spin" size={18} />
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="partner-empty-state" className="py-24 text-center border border-dashed border-black/10">
            <p className="label-overline text-zinc-500">Nothing yet</p>
            <p className="font-serif text-3xl mt-3">No applications match.</p>
            <p className="text-sm text-zinc-500 mt-3">
              {q ? "Try a different search." : "New scouting submissions will appear here as they arrive."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map((a) => (
              <ApplicationCard key={a.id} app={a} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ApplicationCard({ app }) {
  const cover = (app.photos || [])[0];
  const photoCount = (app.photos || []).length;
  const isRecent = isNew(app.created_at);

  return (
    <Link
      to={`/partner/applications/${app.id}`}
      data-testid={`partner-app-card-${app.reference}`}
      className="group block bg-white border border-black/10 hover:border-black transition-colors"
    >
      <div className="relative aspect-[3/4] overflow-hidden bg-zinc-100">
        {cover ? (
          <img
            src={cover}
            alt={app.full_name}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
            style={{ objectPosition: "center 25%" }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-zinc-400 text-xs">
            No photo
          </div>
        )}
        {isRecent && (
          <span
            data-testid="new-badge"
            className="absolute top-3 left-3 bg-black text-white text-[10px] tracking-[0.15em] px-2 py-1 uppercase"
          >
            New
          </span>
        )}
        <span className="absolute bottom-3 right-3 bg-black/70 text-white text-[10px] tracking-wider px-2 py-1 flex items-center gap-1">
          <Camera size={10} /> {photoCount}
        </span>
      </div>
      <div className="p-4">
        <p className="label-overline text-zinc-500">{app.reference}</p>
        <h3 className="font-serif text-xl mt-1 leading-tight">{app.full_name}</h3>
        <div className="mt-2 text-xs text-zinc-600 flex flex-wrap items-center gap-x-3 gap-y-1">
          {app.height_cm && (
            <span className="inline-flex items-center gap-1">
              <Ruler size={11} /> {app.height_cm} cm
            </span>
          )}
          {app.age && <span>{app.age} y</span>}
          {(app.city || app.country) && (
            <span>{[app.city, app.country].filter(Boolean).join(", ")}</span>
          )}
          {app.instagram && (
            <span className="inline-flex items-center gap-1">
              <Instagram size={11} /> {String(app.instagram).replace(/^@/, "")}
            </span>
          )}
        </div>
        <p className="text-[11px] text-zinc-500 mt-3">Applied {fmtDate(app.created_at)}</p>
      </div>
    </Link>
  );
}
